import { Badge, Button, Skeleton, cn } from '@zafroi/ui-kit';
import { Infinity as InfinityIcon, Sparkles } from 'lucide-react';
import type { BotUsage, BotUsageStatus } from '@/types/engage';

/**
 * Slim "plan / usage" strip — sits below the Bot Health card.
 *
 * For BETA / unlimited tenants it stays as a discreet info pill so it does
 * not distract. For paid tiers it shows a horizontal usage meter coloured
 * by `status`, plus the tier badge and an "Upgrade plan" button.
 */

const STATUS_BAR_CLASS: Record<BotUsageStatus, string> = {
  under: 'bg-primary',
  approaching: 'bg-warning',
  over: 'bg-warning',
  hard_cap: 'bg-destructive',
};

const TIER_LABEL: Record<string, string> = {
  STARTER: 'Starter',
  GROWTH: 'Growth',
  SCALE: 'Scale',
  ENTERPRISE: 'Enterprise',
};

export interface UsageStripProps {
  usage: BotUsage | undefined;
  isLoading: boolean;
  onUpgrade: () => void;
}

export function UsageStrip({ usage, isLoading, onUpgrade }: UsageStripProps) {
  if (isLoading || !usage) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-3">
        <Skeleton className="h-4 w-48" />
      </div>
    );
  }

  const { plan, usage: u } = usage;
  const isUnlimited = plan.includedBotTurns == null;

  // BETA + unlimited tiers — discreet pill only.
  if (plan.tier === 'BETA' || isUnlimited) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/30 px-4 py-2.5">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <InfinityIcon className="h-3.5 w-3.5" aria-hidden="true" />
          <span>
            {plan.tier === 'BETA' ? 'Beta plan' : TIER_LABEL[plan.tier] ?? plan.tier}
            {' '}· unlimited bot turns
          </span>
        </span>
        {/*
          Don't push beta tenants toward an upgrade — the badge above is the
          only nod to plan state, and we keep this strip intentionally quiet.
        */}
        <Badge tone="info" withDot={false}>
          {plan.tier === 'BETA' ? 'Beta' : TIER_LABEL[plan.tier] ?? plan.tier}
        </Badge>
      </div>
    );
  }

  // Paid tier with a finite budget — show the meter.
  const included = plan.includedBotTurns ?? 0;
  const used = u.botTurnsThisMonth;
  const pctRaw = u.percentUsed ?? (included === 0 ? 0 : (used / included) * 100);
  // Cap the visual bar at 100% even when the tenant is over — the over-state
  // is communicated via colour + banner, not by overflowing the bar.
  const barPct = Math.min(100, Math.max(0, pctRaw));
  const status = u.status;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card px-4 py-3 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              {used.toLocaleString()}
            </span>{' '}
            / {included.toLocaleString()} bot turns used this month
          </span>
          <span className="text-[11px] text-muted-foreground">
            {Math.round(pctRaw)}%
          </span>
        </div>
        <div
          role="progressbar"
          aria-valuenow={Math.round(barPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Bot turns used this month"
          className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted"
        >
          <div
            className={cn(
              'h-full rounded-full transition-all',
              STATUS_BAR_CLASS[status],
            )}
            style={{ width: `${barPct}%` }}
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Badge
          tone={status === 'under' ? 'info' : 'warning'}
          withDot={false}
        >
          {TIER_LABEL[plan.tier] ?? plan.tier}
        </Badge>
        <Button size="sm" variant="outline" onClick={onUpgrade}>
          <Sparkles className="h-3.5 w-3.5" />
          Upgrade plan
        </Button>
      </div>
    </div>
  );
}
